import { type ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router';

import TextNew from '@cartwhl/ui/base/TextNew';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';

export const NEW_APPS_BANNER_DISMISSED = 'homepage-apps-note-dismissed';
export const NEW_APPS_BANNER_DEV_TTL_MS = 1000 * 60 * 30;

function getNewAppsBannerDismissedAt() {
  if (typeof sessionStorage === 'undefined') return null;

  const rawDismissedAt = sessionStorage.getItem(NEW_APPS_BANNER_DISMISSED);
  if (!rawDismissedAt) return null;

  const dismissedAt = Number(rawDismissedAt);
  return Number.isFinite(dismissedAt) ? dismissedAt : null;
}

function getNewAppsBannerDismissed() {
  const dismissedAt = getNewAppsBannerDismissedAt();
  if (dismissedAt === null) return false;

  if (!import.meta.env.DEV) {
    return true;
  }

  if (Date.now() - dismissedAt > NEW_APPS_BANNER_DEV_TTL_MS) {
    sessionStorage.removeItem(NEW_APPS_BANNER_DISMISSED);
    return false;
  }

  return true;
}

export function useNewAppsBanner() {
  const [dismissed, setDismissed] = useState(() => getNewAppsBannerDismissed());
  const showBanner = !dismissed;

  useEffect(() => {
    if (!import.meta.env.DEV || !dismissed) {
      return;
    }

    const dismissedAt = getNewAppsBannerDismissedAt();
    if (dismissedAt === null) {
      return;
    }

    const remainingMs = NEW_APPS_BANNER_DEV_TTL_MS - (Date.now() - dismissedAt);

    if (remainingMs <= 0) {
      sessionStorage.removeItem(NEW_APPS_BANNER_DISMISSED);
      setDismissed(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      sessionStorage.removeItem(NEW_APPS_BANNER_DISMISSED);
      setDismissed(false);
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [dismissed]);

  const dismissBanner = () => {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(NEW_APPS_BANNER_DISMISSED, String(Date.now()));
    }
    setDismissed(true);
  };

  return { showBanner, dismissBanner };
}

export interface NewAppsBannerProps {
  onDismiss: () => void;
  comicHref: string;
  /** When true, Comic 4 uses client-side router navigation (homepage). */
  comicLinkAsRouter?: boolean;
}

export function NewAppsBanner({
  onDismiss,
  comicHref,
  comicLinkAsRouter = false,
}: NewAppsBannerProps) {
  const comicLink: ReactNode = comicLinkAsRouter ? (
    <Link
      to={comicHref}
      className="text-white underline transition-colors hover:text-white"
    >
      Comic 4
    </Link>
  ) : (
    <a
      href={comicHref}
      className="text-white underline transition-colors hover:text-white"
    >
      Comic 4
    </a>
  );

  return (
    <div
      className="relative flex min-h-12 w-full items-center justify-center bg-green-50 px-12 py-2"
      role="note"
    >
      <TextNew
        as="p"
        token="14/medium"
        className="min-w-0 flex-1 text-center text-[#D5FFDE]"
      >
        {comicLink}
        {', '}
        our performance capture model — with multi-character, expressive faces,
        hands, and camera moves — is available now. Try it today!
      </TextNew>

      <button
        type="button"
        onClick={onDismiss}
        className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-white transition-opacity hover:opacity-80"
        aria-label="Dismiss app announcement"
      >
        <CrossIcon className="h-5 w-5" />
      </button>
    </div>
  );
}
